import { Line } from "react-chartjs-2"
import styled from "styled-components"

import tooltipIcon from "assets/svg/tooltip-icon.svg"

import { Tooltip } from "components/common/Tooltip"

import { type ChartContext, createGradient, sparklineOptions } from "./chartConfig"
import { TrendMetric } from "./trendsData"

const toneColors = {
  positive: { line: "#22c55e", fill: "rgba(34, 197, 94, 0.3)", text: "var(--status-success-text)" },
  negative: { line: "#ef4444", fill: "rgba(239, 68, 68, 0.3)", text: "var(--status-danger-text)" },
  neutral: { line: "#94a3b8", fill: "rgba(148, 163, 184, 0.3)", text: "var(--text-muted)" },
} as const

const Card = styled.div`
  flex: 1 0 0;
  min-width: min(220px, 100%);
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border-radius: 16px;
  border: 1px solid var(--border-default);
  background: var(--surface-1);
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    opacity: 0.5;
  }
`

const Body = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
`

const Value = styled.div`
  font-size: 28px;
  font-weight: 600;
  line-height: 1.1;
  color: var(--text-primary);
`

const Change = styled.div<{ $tone: keyof typeof toneColors }>`
  margin-top: 8px;
  font-size: 12px;
  color: var(--text-muted);

  strong {
    font-weight: 500;
    color: ${({ $tone }) => toneColors[$tone].text};
  }
`

const ChartArea = styled.div`
  width: 45%;
  max-width: 120px;
  height: 56px;
`

interface Props {
  metric: TrendMetric
}

const CardTooltip = styled(Tooltip)`
  span::after {
    content: "";
    display: inline-block;
    width: 16px;
    height: 16px;
    top: 3px;
    background-color: #64748b;
    mask: url("${tooltipIcon}") center / contain no-repeat;
  }
`

export const TrendCard = ({ metric }: Props) => {
  const tone = toneColors[metric.tone]
  const tooltipId = `agent-trend-${metric.title.toLowerCase().replace(/\W+/g, "-")}`

  return (
    <Card>
      <Header>
        {metric.title}
        <CardTooltip id={tooltipId} text="" variant="dark" orientation="top">
          {metric.tooltip}
        </CardTooltip>
      </Header>
      <Body>
        <div>
          <Value>{metric.value}</Value>
          <Change $tone={metric.tone}>
            {metric.change && (
              <>
                <strong>
                  {metric.direction ? `${metric.direction === "up" ? "↑" : "↓"} ${metric.change}` : metric.change}
                </strong>{" "}
              </>
            )}
            {metric.helper}
          </Change>
        </div>
        <ChartArea>
          <Line
            data={{
              labels: metric.trend.map((_, index) => index),
              datasets: [
                {
                  data: metric.trend,
                  borderColor: tone.line,
                  borderWidth: 2,
                  fill: true,
                  backgroundColor: (context: ChartContext) => createGradient(context, tone.fill),
                  tension: 0.45,
                  pointRadius: 0,
                  spanGaps: true,
                },
              ],
            }}
            options={sparklineOptions}
          />
        </ChartArea>
      </Body>
    </Card>
  )
}
